import React, { useState, useEffect, useCallback, useRef } from "react";
import { Icon, Button, Input } from "semantic-ui-react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchSettings, updateSettings } from "../settingsActions";
import { useSubscriptionStatus } from "../../../../../hooks/useSubscriptionStatus";
import SubscriptionRestriction from "../../../subscriptionRestriction/SubscriptionRestriction";
import "./General.css";

const General = ({
  settings,
  loading,
  updating,
  error,
  fetchSettings,
  updateSettings
}) => {
  const { t } = useTranslation();
  const { isSuspended } = useSubscriptionStatus();
  const history = useHistory();
  const _companyName = JSON.parse(localStorage.getItem("selectedCompany"));

  // Local state for form fields
  const [companyName, setCompanyName] = useState(_companyName?.name || "");
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  // Debouncing refs
  const debounceTimeouts = useRef({});

  // Track which fields are being actively edited to prevent overwriting
  const editingFields = useRef(new Set());

  // Validation states
  const [validationErrors, setValidationErrors] = useState({});

  // Debounced update function
  const debouncedUpdateSettings = useCallback((fieldName, value, delay = 500) => {
    // Clear existing timeout for this field
    if (debounceTimeouts.current[fieldName]) {
      clearTimeout(debounceTimeouts.current[fieldName]);
    }

    // Set new timeout
    debounceTimeouts.current[fieldName] = setTimeout(async () => {
      try {
        await updateSettings(fieldName, value);
        // Clear the timeout reference and remove from editing set
        delete debounceTimeouts.current[fieldName];
        editingFields.current.delete(fieldName);
      } catch (error) {
        // Clear the timeout reference even on error and remove from editing set
        delete debounceTimeouts.current[fieldName];
        editingFields.current.delete(fieldName);
      }
    }, delay);
  }, [updateSettings]);

  // Redirect to billing if subscription is suspended
  useEffect(() => {
    if (isSuspended) {
      history.replace("/settings/company?tab=billing");
    }
  }, [isSuspended, history]);

  // Fetch settings on component mount
  useEffect(() => {
    if (!isSuspended) {
      fetchSettings();
    }
  }, [fetchSettings, isSuspended]);

  // Update local state when settings are loaded (only if not currently editing)
  useEffect(() => {
    if (settings) {
      // Only update fields that are not currently being edited
      if (!editingFields.current.has('companyName')) {
        setCompanyName(settings.name || _companyName?.name || "");
      }
      if (!editingFields.current.has('companyEmail')) {
        setCompanyEmail(settings.companyEmail || "");
      }
      if (!editingFields.current.has('companyPhone')) {
        setCompanyPhone(settings.phoneNumber || "");
      }
      if (!editingFields.current.has('companyAddress')) {
        setCompanyAddress(settings.address || "");
      }
    }
  }, [settings, _companyName?.name]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  // Show restriction message if subscription is suspended
  if (isSuspended) {
    return (
      <div style={{ padding: "20px" }}>
        <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessSettings" />
      </div>
    );
  }

  // Validation functions
  const validateField = (field, value) => {
    const errors = { ...validationErrors };

    switch (field) {
      case 'companyName':
        if (!value || value.trim().length === 0) {
          errors.companyName = 'Company name is required';
        } else if (value.length > 250) {
          errors.companyName = 'Company name must be 250 characters or less';
        } else {
          delete errors.companyName;
        }
        break;

      case 'companyEmail':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && value.trim().length > 0) {
          if (!emailRegex.test(value)) {
            errors.companyEmail = 'Please enter a valid email address';
          } else if (value.length > 255) {
            errors.companyEmail = 'Email must be 255 characters or less';
          } else {
            delete errors.companyEmail;
          }
        } else {
          delete errors.companyEmail; // Email is optional
        }
        break;

      case 'companyPhone':
        // Allow + at start, then only digits, spaces, hyphens, parentheses
        const phoneRegex = /^\+?[\d\s\-\(\)]+$/;
        if (value && value.trim().length > 0) {
          if (!phoneRegex.test(value)) {
            errors.companyPhone = 'Phone number can only contain digits, +, spaces, hyphens, and parentheses';
          } else if (value.replace(/[\s\-\(\)]/g, '').length < 7) {
            errors.companyPhone = 'Phone number must be at least 7 digits';
          } else if (value.length > 20) {
            errors.companyPhone = 'Phone number must be 20 characters or less';
          } else {
            delete errors.companyPhone;
          }
        } else {
          delete errors.companyPhone; // Phone is optional
        }
        break;

      case 'companyAddress':
        if (value && value.length > 500) {
          errors.companyAddress = 'Address must be 500 characters or less';
        } else {
          delete errors.companyAddress;
        }
        break;

      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form field changes with auto-save
  const handleFieldChange = async (field, value) => {
    // Mark field as being edited to prevent overwriting from API response
    editingFields.current.add(field);

    // Validate field before updating state (only for text fields)
    const isValid = validateField(field, value);

    // Update local state immediately for UI responsiveness
    switch (field) {
      case 'companyName':
        setCompanyName(value);
        break;
      case 'companyEmail':
        setCompanyEmail(value);
        break;
      case 'companyPhone':
        setCompanyPhone(value);
        break;
      case 'companyAddress':
        setCompanyAddress(value);
        break;
      default:
        break;
    }

    // Map field names to API field names
    const fieldMapping = {
      'companyName': 'Name',
      'companyEmail': 'CompanyEmail',
      'companyPhone': 'PhoneNumber',
      'companyAddress': 'Address',
    };

    // Auto-save to API with debouncing (only if field is valid)
    if (fieldMapping[field] && isValid) {
      // Handle regular fields with different delays (only if valid)
      const delay = ['companyName', 'companyEmail', 'companyPhone', 'companyAddress'].includes(field) ? 800 : 500;
      debouncedUpdateSettings(fieldMapping[field], value, delay);
    }
  };

  const timezoneOptions = [
    { key: "utc-8", text: "UTC-8 (PST)", value: "UTC-8" },
    { key: "utc-7", text: "UTC-7 (MST)", value: "UTC-7" },
    { key: "utc-6", text: "UTC-6 (CST)", value: "UTC-6" },
    { key: "utc-5", text: "UTC-5 (EST)", value: "UTC-5" },
    { key: "utc+0", text: "UTC+0 (GMT)", value: "UTC+0" },
    { key: "utc+1", text: "UTC+1 (CET)", value: "UTC+1" },
    { key: "utc+2", text: "UTC+2 (EET)", value: "UTC+2" },
    { key: "utc+5", text: "UTC+5 (IST)", value: "UTC+5" },
    { key: "utc+8", text: "UTC+8 (CST)", value: "UTC+8" },
  ];

  const dateFormatOptions = [
    { key: "mm-dd-yyyy", text: "MM/DD/YYYY", value: "MM/DD/YYYY" },
    { key: "dd-mm-yyyy", text: "DD/MM/YYYY", value: "DD/MM/YYYY" },
    { key: "yyyy-mm-dd", text: "YYYY-MM-DD", value: "YYYY-MM-DD" },
    { key: "mm-dd-yy", text: "MM/DD/YY", value: "MM/DD/YY" },
  ];

  const themeOptions = [
    { key: "light", text: "Light Theme", value: "light" },
    { key: "dark", text: "Dark Theme", value: "dark" },
    { key: "auto", text: "Auto (System)", value: "auto" },
  ];

  const colorOptions = [
    { key: "blue", text: "Blue (#667eea)", value: "#667eea" },
    { key: "purple", text: "Purple (#8b5cf6)", value: "#8b5cf6" },
    { key: "green", text: "Green (#10b981)", value: "#10b981" },
    { key: "orange", text: "Orange (#f59e0b)", value: "#f59e0b" },
    { key: "red", text: "Red (#ef4444)", value: "#ef4444" },
    { key: "pink", text: "Pink (#ec4899)", value: "#ec4899" },
  ];

  // Show loading state
  if (loading) {
    return (
      <div className="workspace-settings">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Icon name="spinner" loading size="large" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="workspace-settings">
        <div style={{ textAlign: 'center', padding: '50px', color: '#e53e3e' }}>
          <Icon name="exclamation triangle" size="large" />
          <p>{t("settings.general.errorLoadingSettings")} {error}</p>
          <Button onClick={() => fetchSettings()}>{t("settings.general.retry")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="workspace-settings">
      {/* Workspace Information Section */}
      <div className="settings-section">
        <div className="section-header">
          <div className="section-icon">
            <Icon name="building" className="m-0 mb-1" />
          </div>
          <div className="section-content">
            <h3 className="section-title">{t("settings.general.companyInformation")}</h3>
            <p className="section-description">
              {t("settings.general.companyInfoDescription")}
            </p>
          </div>
        </div>

        <div className="settings-grid">
          {/* Company Information Section */}
          <div className="setting-card">
            <div className="setting-header">
              <span className="setting-icon">
                <Icon name="building outline" className="m-0 mb-1" />
              </span>
              <div className="setting-info">
                <h4 className="setting-title">{t("settings.general.companyName")}</h4>
                <p className="setting-description">
                  {t("settings.general.companyNameDescription")}
                </p>
              </div>
            </div>
            <div className="setting-control">
              <Input
                fluid
                placeholder={t("settings.general.companyNamePlaceholder")}
                value={companyName}
                onChange={(e) => handleFieldChange('companyName', e.target.value)}
                className={`modern-input ${validationErrors.companyName ? 'error' : ''}`}
                maxLength={100}
              />
              {validationErrors.companyName && (
                <div className="validation-error">
                  <Icon name="exclamation circle" />
                  {validationErrors.companyName}
                </div>
              )}
            </div>
          </div>

          <div className="setting-card">
            <div className="setting-header">
              <span className="setting-icon">
                <Icon name="mail outline" className="m-0 mb-1" />
              </span>
              <div className="setting-info">
                <h4 className="setting-title">{t("settings.general.companyEmail")}</h4>
                <p className="setting-description">
                  {t("settings.general.companyEmailDescription")}
                </p>
              </div>
            </div>
            <div className="setting-control">
              <Input
                fluid
                placeholder={t("settings.general.companyEmailPlaceholder")}
                value={companyEmail}
                onChange={(e) => handleFieldChange('companyEmail', e.target.value)}
                className={`modern-input ${validationErrors.companyEmail ? 'error' : ''}`}
                type="email"
                maxLength={255}
              />
              {validationErrors.companyEmail && (
                <div className="validation-error">
                  <Icon name="exclamation circle" />
                  {validationErrors.companyEmail}
                </div>
              )}
            </div>
          </div>

          <div className="setting-card">
            <div className="setting-header">
              <span className="setting-icon">
                <Icon name="phone" className="m-0 mb-1" />
              </span>
              <div className="setting-info">
                <h4 className="setting-title">{t("settings.general.companyPhone")}</h4>
                <p className="setting-description">
                  {t("settings.general.companyPhoneDescription")}
                </p>
              </div>
            </div>
            <div className="setting-control">
              <Input
                fluid
                placeholder={t("settings.general.companyPhonePlaceholder")}
                value={companyPhone}
                onChange={(e) => handleFieldChange('companyPhone', e.target.value)}
                className={`modern-input ${validationErrors.companyPhone ? 'error' : ''}`}
                type="tel"
                maxLength={20}
              />
              {validationErrors.companyPhone && (
                <div className="validation-error">
                  <Icon name="exclamation circle" />
                  {validationErrors.companyPhone}
                </div>
              )}
            </div>
          </div>

          <div className="setting-card">
            <div className="setting-header">
              <span className="setting-icon">
                <Icon name="map marker alternate" className="m-0 mb-1" />
              </span>
              <div className="setting-info">
                <h4 className="setting-title">{t("settings.general.companyAddress")}</h4>
                <p className="setting-description">
                  {t("settings.general.companyAddressDescription")}
                </p>
              </div>
            </div>
            <div className="setting-control">
              <Input
                fluid
                placeholder={t("settings.general.companyAddressPlaceholder")}
                value={companyAddress}
                onChange={(e) => handleFieldChange('companyAddress', e.target.value)}
                className={`modern-input ${validationErrors.companyAddress ? 'error' : ''}`}
                maxLength={500}
              />
              {validationErrors.companyAddress && (
                <div className="validation-error">
                  <Icon name="exclamation circle" />
                  {validationErrors.companyAddress}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Map Redux state to props
const mapStateToProps = (state) => ({
  settings: state.settingsReducer,
  loading: state.settingsReducer.loading,
  updating: state.settingsReducer.updating,
  error: state.settingsReducer.error,
});

// Map Redux actions to props
const mapDispatchToProps = {
  fetchSettings,
  updateSettings,
};

export default connect(mapStateToProps, mapDispatchToProps)(General);
